import { INotification, Notification } from '@app/models/Notification'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import {
  ActionTypes as NotificationsSocketActionTypes,
  IUpdateNotificationReceivedAction,
} from '@app/utils/notificationsSocket'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface INotificationsListState {
  readonly error?: string
  readonly isLoading: boolean
  readonly itemChangedIndex?: number
  readonly items: List<INotification>
  readonly totalItems: number
  readonly unseenOnly: boolean
}

const NotificationsListStateRecord = Record({
  error: undefined,
  isLoading: false,
  itemChangedIndex: undefined,
  items: List<Notification>(),
  totalItems: 0,
  unseenOnly: false,
})

export class NotificationsListState extends NotificationsListStateRecord
  implements INotificationsListState {
  public static fromJS(initialState?: Partial<INotificationsListState>) {
    if (!initialState) {
      return new NotificationsListState()
    }

    const { items, ...rest } = initialState
    return new NotificationsListState({
      items: items ? List(items.map((i) => Notification.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isLoading: boolean
  public readonly itemChangedIndex?: number
  public readonly items: List<INotification>
  public readonly totalItems: number
  public readonly unseenOnly: boolean

  constructor(props?: Partial<NotificationsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadNotifications = 'NOTIFICATIONS_LIST::LOAD_NOTIFICATIONS',
  LoadNotificationsFailure = 'NOTIFICATIONS_LIST::LOAD_NOTIFICATIONS_FAILURE',
  LoadNotificationsRequest = 'NOTIFICATIONS_LIST::LOAD_NOTIFICATIONS_REQUEST',
  LoadNotificationsSuccess = 'NOTIFICATIONS_LIST::LOAD_NOTIFICATIONS_SUCCESS',
  ResetItemChangedIndex = 'NOTIFICATIONS_LIST::RESET_ITEM_CHANGED_INDEX',
  ResetState = 'NOTIFICATIONS_LIST::RESET_STATE',
  SetUnseenOnly = 'NOTIFICATIONS_LIST::SET_UNSEEN_ONLY',
}

interface ILoadNotificationsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActionTypes.LoadNotifications
}

interface ILoadNotificationsFailureAction {
  error: string
  type: ActionTypes.LoadNotificationsFailure
}

interface ILoadNotificationsRequestAction {
  type: ActionTypes.LoadNotificationsRequest
}

interface ILoadNotificationsSuccessAction {
  append: boolean
  items: Notification[]
  totalItems: number
  type: ActionTypes.LoadNotificationsSuccess
}

interface IResetItemChangedIndexAction {
  type: ActionTypes.ResetItemChangedIndex
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetUnseenOnlyAction {
  type: ActionTypes.SetUnseenOnly
  unseenOnly: boolean
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadNotificationsFailureAction
  | ILoadNotificationsRequestAction
  | ILoadNotificationsSuccessAction
  | IResetItemChangedIndexAction
  | IResetStateAction
  | ISetUnseenOnlyAction
  | IUpdateNotificationReceivedAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadNotifications(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadNotificationsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActionTypes.LoadNotifications,
  }
}

function loadNotificationsFailure(
  error: string,
): ILoadNotificationsFailureAction {
  return {
    error,
    type: ActionTypes.LoadNotificationsFailure,
  }
}

function loadNotificationsRequest(): ILoadNotificationsRequestAction {
  return {
    type: ActionTypes.LoadNotificationsRequest,
  }
}

function loadNotificationsSuccess(
  items: Notification[],
  append: boolean,
  totalItems: number,
): ILoadNotificationsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActionTypes.LoadNotificationsSuccess,
  }
}

function resetItemChangedIndex(): IResetItemChangedIndexAction {
  return { type: ActionTypes.ResetItemChangedIndex }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setUnseenOnly(unseenOnly: boolean): ISetUnseenOnlyAction {
  return {
    type: ActionTypes.SetUnseenOnly,
    unseenOnly,
  }
}

export const actionCreators = {
  loadNotifications,
  resetItemChangedIndex,
  resetState,
  setUnseenOnly,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<NotificationsListState> = (
  state: NotificationsListState = new NotificationsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadNotificationsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as NotificationsListState

    case ActionTypes.LoadNotificationsRequest:
      return state.set('isLoading', true) as NotificationsListState

    case ActionTypes.LoadNotificationsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Notification>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as NotificationsListState

    case ActionTypes.ResetItemChangedIndex:
      return state.set('itemChangedIndex', undefined) as NotificationsListState

    case ActionTypes.ResetState:
      return new NotificationsListState()

    case ActionTypes.SetUnseenOnly:
      return state.set(
        'unseenOnly',
        action.unseenOnly,
      ) as NotificationsListState

    case NotificationsSocketActionTypes.UpdateNotificationReceived: {
      const index = state.items.findIndex(
        (i) => i !== undefined && i.id === action.notification.id,
      )
      if (index >= 0) {
        return state
          .set(
            'itemChangedIndex',
            Math.min(state.itemChangedIndex || index, index),
          )
          .setIn(
            ['items', index],
            action.notification,
          ) as NotificationsListState
      } else {
        return state
      }
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadNotificationsSaga(action: ILoadNotificationsAction) {
  try {
    yield put(loadNotificationsRequest())

    const currentState: NotificationsListState = yield select(
      selectModuleState(),
    )

    const query: string[] = ['context=All']

    if (currentState.unseenOnly) {
      query.push(
        `unseenOnly=${encodeURIComponent(currentState.unseenOnly.toString())}`,
      )
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const url = `/api/Notifications/My${
      query.length > 0 ? `?${query.join('&')}` : ''
    }`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const items: Notification[] = result.results.map((c: any) =>
        Notification.fromJS(c),
      )
      yield put(
        loadNotificationsSuccess(items, action.append, result.paging.total),
      )
    } else {
      yield put(loadNotificationsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadNotificationsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadNotifications() {
  yield takeLatest(ActionTypes.LoadNotifications, loadNotificationsSaga)
}

export function* notificationsListRootSaga() {
  yield fork(watchLoadNotifications)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): NotificationsListState => state.notificationsList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItemChangedIndex = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.itemChangedIndex,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )

export const selectUnseenOnly = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.unseenOnly,
  )
